import { DOC_SCHEMA } from '@adornis/prosemirror-editor/nodes/doc/schema.js';
import type { NodeSpec } from 'prosemirror-model';
import type { WebsiteBuilderRecord, WebsiteComponentAttrs } from './types.js';

export const WEBSITE_COMPONENT_KEY = 'websiteComponent';
export const WEBSITE_DOCUMENT_VERSION = 1;

export const createWebsiteComponentId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ? `wb-${uuid}` : `wb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const defaultWebsiteComponentAttrs = (): WebsiteComponentAttrs => ({
  id: createWebsiteComponentId(),
  componentType: 'unknown',
  componentVersion: 1,
  props: {} satisfies WebsiteBuilderRecord,
  styles: {} satisfies WebsiteBuilderRecord,
  responsive: {} satisfies WebsiteBuilderRecord,
  hidden: false,
  locked: false,
});

const parseRecordAttribute = (element: HTMLElement, name: string): WebsiteBuilderRecord => {
  const value = element.getAttribute(name);
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as WebsiteBuilderRecord) : {};
  } catch {
    return {};
  }
};

export const WEBSITE_DOCUMENT_SCHEMA = (): NodeSpec => ({
  ...DOC_SCHEMA(),
  content: `${WEBSITE_COMPONENT_KEY}+`,
  attrs: {
    ...DOC_SCHEMA().attrs,
    formatVersion: { default: WEBSITE_DOCUMENT_VERSION },
    projectId: { default: '' },
  },
  toDOM: node => [
    'node-doc',
    {
      lang: node.attrs.lang,
      style: node.attrs.style,
      'size-breakpoints': node.attrs['size-breakpoints'],
      'global-settings-class-name': node.attrs['global-settings-class-name'],
      'data-format-version': String(node.attrs.formatVersion ?? WEBSITE_DOCUMENT_VERSION),
      'data-project-id': String(node.attrs.projectId ?? ''),
    },
    0,
  ],
});

export const WEBSITE_COMPONENT_SCHEMA = (): NodeSpec => ({
  group: 'block',
  content: 'block*',
  draggable: true,
  selectable: true,
  defining: true,
  isolating: true,
  attrs: {
    id: { default: '' },
    componentType: { default: 'unknown' },
    componentVersion: { default: 1 },
    props: { default: {} },
    styles: { default: {} },
    responsive: { default: {} },
    hidden: { default: false },
    locked: { default: false },
  },
  parseDOM: [
    {
      tag: 'website-builder-component',
      getAttrs: dom => {
        if (!(dom instanceof HTMLElement)) return false;
        return {
          id: dom.dataset.componentId || createWebsiteComponentId(),
          componentType: dom.dataset.componentType || 'unknown',
          componentVersion: Number(dom.dataset.componentVersion || 1),
          props: parseRecordAttribute(dom, 'data-props'),
          styles: parseRecordAttribute(dom, 'data-styles'),
          responsive: parseRecordAttribute(dom, 'data-responsive'),
          hidden: dom.dataset.hidden === 'true',
          locked: dom.dataset.locked === 'true',
        } satisfies WebsiteComponentAttrs;
      },
    },
  ],
  toDOM: node => [
    'website-builder-component',
    {
      'data-component-id': String(node.attrs.id),
      'data-component-type': String(node.attrs.componentType),
      'data-component-version': String(node.attrs.componentVersion),
      'data-props': JSON.stringify(node.attrs.props ?? {}),
      'data-styles': JSON.stringify(node.attrs.styles ?? {}),
      'data-responsive': JSON.stringify(node.attrs.responsive ?? {}),
      'data-hidden': String(node.attrs.hidden === true),
      'data-locked': String(node.attrs.locked === true),
    },
    0,
  ],
});

export const isWebsiteComponentAttrs = (value: unknown): value is WebsiteComponentAttrs => {
  if (!value || typeof value !== 'object') return false;
  const attrs = value as Partial<WebsiteComponentAttrs>;
  return (
    typeof attrs.id === 'string' &&
    typeof attrs.componentType === 'string' &&
    typeof attrs.componentVersion === 'number' &&
    typeof attrs.props === 'object' &&
    attrs.props !== null &&
    typeof attrs.styles === 'object' &&
    attrs.styles !== null &&
    typeof attrs.responsive === 'object' &&
    attrs.responsive !== null &&
    typeof attrs.hidden === 'boolean' &&
    typeof attrs.locked === 'boolean'
  );
};
